/**
 * Firebase Analytics — thin wrapper around @capacitor-firebase/analytics.
 *
 * Unlike AdMob (native-only SDK, see lib/ads.ts), this plugin's web
 * implementation wraps `firebase/analytics` itself, so a single call site
 * works on both the Android app and the web/iOS PWA — no platform branching
 * needed here. `initTravelixFirebase()` must have already run (main.tsx
 * boot order) since the web implementation calls `getAnalytics()` with no
 * app argument, which resolves the default app.
 *
 * Every call is fire-and-forget and swallows its own errors: analytics must
 * never be able to break a user-facing flow (a slow network, a browser that
 * blocks IndexedDB in private mode, etc.).
 */

import { FirebaseAnalytics } from "@capacitor-firebase/analytics";

const isDev = import.meta.env.DEV;

/**
 * Call once at boot, after initTravelixFirebase(). Dev builds keep
 * collection off so local/staging sessions don't skew production DAU —
 * flip DEBUG_ANALYTICS_IN_DEV below if you need to verify event wiring
 * against the real Firebase console.
 */
const DEBUG_ANALYTICS_IN_DEV = false;

export async function initAnalytics(): Promise<void> {
  try {
    await FirebaseAnalytics.setEnabled({ enabled: !isDev || DEBUG_ANALYTICS_IN_DEV });
  } catch (err) {
    if (isDev) console.warn("[analytics] setEnabled failed", err);
  }
}

/** Log a custom event. Params must be string/number/boolean — no PII. */
export function logAnalyticsEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  FirebaseAnalytics.logEvent({ name, params }).catch((err) => {
    if (isDev) console.warn(`[analytics] logEvent(${name}) failed`, err);
  });
}

/** Screen-view — called from the router on every route change (see router.tsx). */
export function trackScreenView(screenName: string): void {
  FirebaseAnalytics.setCurrentScreen({ screenName }).catch((err) => {
    if (isDev) console.warn("[analytics] setCurrentScreen failed", err);
  });
}

/** Mirrors auth state so events can be attributed to a user in the console. */
export function setAnalyticsUserId(userId: string | null): void {
  FirebaseAnalytics.setUserId({ userId }).catch((err) => {
    if (isDev) console.warn("[analytics] setUserId failed", err);
  });
}
